import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import './data-item-list.js'
import './data-set-importer.js'
import '../../components/data-entry-form.js'
import '../../components/checklist-entry-form.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { asyncReplace } from 'lit/directives/async-replace.js'
import { connect } from 'pwa-helpers/connect-mixin'

import { DataGrist, FetchOption, getEditor, getRenderer, ImexConfig } from '@operato/data-grist'
import { OxDataUseCase } from '@operato/dataset'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { notify, openPopup } from '@operato/layout'
import { PageView, store, navigate } from '@operato/shell'
import { CommonGristStyles, CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice, sleep, adjustFilters } from '@operato/utils'

import { p13n } from '@operato/p13n'
import { provider } from '@things-factory/board-ui/dist-client/board-provider.js'

const MIN = 60
const HOUR = 60 * MIN
const DAY = 24 * HOUR

const ENTRY_TYPES = [
  { display: '', value: '' },
  { display: 'Generated', value: 'generated' },
  { display: 'Checklist', value: 'checklist' },
  { display: 'Board', value: 'board' },
  { display: 'CustomElement', value: 'custom-element' },
  { display: 'Page', value: 'page' },
  { display: 'External URL', value: 'external' }
]

const MONITOR_TYPES = [
  { display: '', value: '' },
  { display: 'Generated', value: 'generated' },
  { display: 'Checklist', value: 'checklist' },
  { display: 'Board', value: 'board' },
  { display: 'CustomElement', value: 'custom-element' },
  { display: 'Page', value: 'page' },
  { display: 'External URL', value: 'external' }
]

const REPORT_TYPES = [
  { display: '', value: '' },
  { display: 'Generated', value: 'generated' },
  { display: 'Embed', value: 'embed' },
  { display: 'CustomElement', value: 'custom-element' },
  { display: 'Page', value: 'page' },
  { display: 'External URL', value: 'external' },
  { display: 'Jasper', value: 'jasper' },
  { display: 'Shiny', value: 'shiny' }
]

const SUMMARY_PERIOD_TYPES = [
  { display: '', value: '' },
  { display: 'Hour', value: 'hour' },
  { display: 'WorkShift', value: 'work-shift' },
  { display: 'WorkDate', value: 'work-date' },
  { display: 'Day', value: 'day' }
]

const USECASE_OPTIONS = () => {
  return ['', ...OxDataUseCase.getUseCaseNames()].map(name => {
    return {
      display: name,
      value: name
    }
  })
}

export const showEntryView = async (columns, data, column, record, rowIndex) => {
  const { name, entryType, entryView } = record
  const title = `${name} - ${i18next.t('title.data-entry-form')}`

  switch (entryType) {
    case 'generated':
      openPopup(html` <data-entry-form .dataSet=${record} style="background-color: white;"></data-entry-form> `, {
        closable: true,
        backdrop: true,
        size: 'large',
        title
      })
      break
    case 'checklist':
      openPopup(
        html` <checklist-entry-form .dataSet=${record} style="background-color: white;"></checklist-entry-form> `,
        {
          closable: true,
          backdrop: true,
          size: 'large',
          title
        }
      )
      break

    case 'board':
      const board = {
        id: entryView
      }
      openPopup(
        html`
          <ox-board-viewer
            style="background-color: white;"
            .board=${board}
            .provider=${provider}
            hide-fullscreen
            hide-navigation
          ></ox-board-viewer>
        `,
        {
          closable: true,
          backdrop: true,
          size: 'large',
          title
        }
      )

      // navigate(`board-viewer/${entryView}?interactive=true&title=${title}`)
      break
    case 'page':
      navigate(`${entryView}/${record.id}`)
      break

    case 'external':
      window.open(entryView, '_blank')
      break
  }
}

@customElement('data-set-list-page')
export class DataSetListPage extends connect(store)(p13n(localize(i18next)(PageView))) {
  static styles = [
    ScrollbarStyles,
    CommonGristStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;

        width: 100%;

        --grid-record-emphasized-background-color: #8b0000;
        --grid-record-emphasized-color: #ff6b6b;

        --grid-header-padding: 2px 0 2px 9px;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() gristConfig: any

  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-set list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-set',
      exportable: {
        name: i18next.t('title.data-set list'),
        data: this.exportHandler.bind(this)
      },
      importable: {
        handler: this.importHandler.bind(this)
      },
      actions: [
        {
          title: i18next.t('button.save'),
          action: this._updateDataSet.bind(this),
          icon: 'save'
        },
        {
          title: i18next.t('button.copy'),
          action: this._copyDataSet.bind(this),
          icon: 'content_copy'
        },
        {
          title: i18next.t('button.delete'),
          action: this._deleteDataSet.bind(this),
          icon: 'delete',
          emphasis: {
            danger: true
          }
        }
      ],
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'CARD' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .personalConfigProvider=${this.getPagePreferenceProvider('ox-grist')!}
        .fetchHandler=${this.fetchHandler.bind(this)}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form autofocus without-search></ox-filters-form>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}> </ox-context-page-toolbar>
        </div>

        <ox-grist-personalizer slot="setting"></ox-grist-personalizer>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: {
        fields: ['name', 'description'],
        details: ['schedule', 'active', 'tag']
      },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', multiple: true, fixed: true },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: record => (!record ? 'calendar_add_on' : !record.id || !record.scheduleId ? '' : 'event_available'),
          title: i18next.t('button.schedule-task'),
          handlers: {
            click: (columns, data, column, record, rowIndex) => {
              if (!record || !record.id || (!record.scheduleId && !record.schedule)) {
                return
              }
              if (record.scheduleId) {
                this.stopDataCollectionSchedule(record)
              } else {
                this.startDataCollectionSchedule(record)
              }
            }
          }
        },
        {
          type: 'gutter',
          gutterName: 'button',
          title: record =>
            !record ? i18next.t('button.edit-model') : !record.id ? '' : i18next.t('button.edit-model'),
          icon: record => (!record ? 'reorder' : !record.id ? '' : 'reorder'),
          iconOnly: false,
          width: 96,
          fixed: true,
          handlers: {
            click: (columns, data, column, record, rowIndex) => {
              if (!record.id) return
              const popup = openPopup(html` <data-item-list .dataSet=${record}></data-item-list> `, {
                backdrop: true,
                help: 'dataset/ui/data-item-list',
                size: 'large',
                title: i18next.t('title.data-item list')
              })
              popup.onclosed = () => {
                this.grist.fetch()
              }
            }
          }
        },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: record => (!record ? 'fact_check' : !record.id ? '' : 'fact_check'),
          iconOnly: false,
          width: 96,
          fixed: true,
          title: record =>
            !record ? i18next.t('button.enter-data') : !record.id ? '' : i18next.t('button.enter-data'),
          handlers: {
            click: showEntryView
          }
        },
        {
          type: 'string',
          name: 'name',
          header: i18next.t('field.name'),
          record: {
            editable: true
          },
          filter: 'search',
          sortable: true,
          fixed: true,
          width: 150,
          imex: {
            width: 25,
            header: i18next.t('field.name'),
            type: 'string',
            key: 'name'
          }
        },
        {
          type: 'string',
          name: 'description',
          header: i18next.t('field.description'),
          record: {
            editable: true
          },
          filter: 'search',
          width: 200,
          imex: {
            width: 33,
            header: i18next.t('field.description'),
            type: 'string',
            key: 'description'
          }
        },
        {
          type: 'varname',
          name: 'tag',
          header: i18next.t('field.tag'),
          record: {
            editable: true
          },
          filter: 'search',
          width: 120,
          imex: {
            width: 25,
            header: i18next.t('field.tag'),
            type: 'string',
            key: 'tag'
          }
        },
        {
          type: 'checkbox',
          name: 'active',
          label: true,
          header: i18next.t('field.active'),
          record: {
            editable: true
          },
          filter: true,
          sortable: true,
          width: 60,
          imex: {
            width: 10,
            header: i18next.t('field.active'),
            type: 'checkbox',
            key: 'active'
          }
        },
        {
          type: 'select',
          name: 'type',
          label: true,
          header: i18next.t('field.type'),
          record: {
            editable: true,
            options: [
              {},
              {
                display: i18next.t('text.manually collected'),
                value: 'manual'
              },
              {
                display: i18next.t('text.automatically collected'),
                value: 'automatic'
              }
            ]
          },
          sortable: true,
          filter: true,
          width: 60,
          imex: {
            width: 10,
            header: i18next.t('field.type'),
            type: 'select',
            key: 'type'
          }
        },
        {
          type: 'select',
          name: 'useCase',
          label: true,
          header: i18next.t('field.use-case'),
          record: {
            editable: true,
            options: USECASE_OPTIONS
          },
          sortable: true,
          filter: true,
          width: 80
        },
        {
          type: 'resource-object',
          name: 'dataKeySet',
          header: i18next.t('field.data-key-set'),
          record: {
            editable: true,
            options: {
              queryName: 'dataKeySets'
            }
          },
          filter: false,
          width: 120
        },
        {
          type: 'partition-keys',
          name: 'partitionKeys',
          header: i18next.t('field.partition-keys'),
          record: {
            editable: true,
            options: {
              objectified: true /* transfered as a object type */
            }
          },
          width: 80
        },
        {
          type: 'timezone',
          name: 'timezone',
          header: i18next.t('field.timezone'),
          record: {
            editable: true
          },
          width: 120,
          imex: {
            width: 13,
            header: i18next.t('field.timezone'),
            type: 'select',
            key: 'timezone'
          }
        },
        {
          type: 'crontab',
          name: 'schedule',
          label: true,
          header: i18next.t('field.schedule'),
          record: {
            editable: true,
            options: {
              objectified: true
            }
          },
          width: 80,
          imex: {
            width: 13,
            header: i18next.t('field.schedule'),
            type: 'string',
            key: 'schedule'
          }
        },
        {
          type: 'select',
          name: 'summaryPeriod',
          label: true,
          header: i18next.t('field.summary-period'),
          record: {
            editable: true,
            options: SUMMARY_PERIOD_TYPES
          },
          width: 80
        },
        {
          type: 'string',
          name: 'nextSummarySchedule',
          header: i18next.t('field.next-summary-schedule'),
          record: {
            editable: false,
            renderer: (value, column, record, rowIndex, field) => {
              return html` <span>${asyncReplace(this.summaryScheduleTimer(record.dueAt))}</span>`
            }
          },
          sortable: false,
          width: 140
        },
        {
          type: 'duration',
          name: 'timeLimit',
          header: i18next.t('field.time-limit'),
          record: {
            editable: true
          },
          width: 80
        },
        {
          type: 'resource-object',
          name: 'normalScenario',
          header: {
            renderer: i18next.t('field.normal-scenario'),
            group: i18next.t('text.scenario')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup scenario'),
              queryName: 'scenarios',
              columns: [
                { name: 'id', hidden: true },
                { name: 'name', header: i18next.t('field.name'), filter: 'search' },
                { name: 'description', header: i18next.t('field.description'), filter: 'search' }
              ],
              list: { fields: ['name', 'description'] }
            }
          },
          sortable: true,
          filter: false,
          width: 120
        },
        {
          type: 'resource-object',
          name: 'outlierScenario',
          header: {
            renderer: i18next.t('field.outlier-scenario'),
            group: i18next.t('text.scenario')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup scenario'),
              queryName: 'scenarios',
              columns: [
                { name: 'id', hidden: true },
                { name: 'name', header: i18next.t('field.name'), filter: 'search' },
                { name: 'description', header: i18next.t('field.description'), filter: 'search' }
              ],
              list: { fields: ['name', 'description'] }
            }
          },
          sortable: true,
          filter: false,
          width: 120
        },
        {
          type: 'resource-object',
          name: 'supervisoryRole',
          header: {
            renderer: i18next.t('field.supervisory-role'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup role'),
              queryName: 'roles'
            }
          },
          filter: false,
          width: 120
        },
        {
          type: 'resource-object',
          name: 'entryRole',
          header: {
            renderer: i18next.t('field.entry-role'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup role'),
              queryName: 'roles'
            }
          },
          width: 120
        },
        {
          type: 'resource-object',
          name: 'resolverRole',
          header: {
            renderer: i18next.t('field.resolver-role'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true,
            options: {
              title: i18next.t('title.lookup role'),
              queryName: 'roles'
            }
          },
          filter: false,
          width: 120
        },
        {
          type: 'approval-line',
          name: 'reviewApprovalLine',
          header: {
            renderer: i18next.t('field.review-approval-line'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true
          },
          width: 80
        },
        {
          type: 'approval-line',
          name: 'outlierApprovalLine',
          header: {
            renderer: i18next.t('field.outlier-approval-line'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true
          },
          width: 80
        },
        {
          type: 'checkbox',
          name: 'requiresReview',
          label: true,
          header: {
            renderer: i18next.t('field.requires-review'),
            group: i18next.t('field.role')
          },
          record: {
            editable: true
          },
          filter: true,
          width: 60,
          imex: {
            width: 10,
            header: i18next.t('field.requires-review'),
            type: 'checkbox',
            key: 'requiresReview'
          }
        },
        {
          type: 'select',
          name: 'entryType',
          label: true,
          header: {
            renderer: i18next.t('field.entry-type'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            options: ENTRY_TYPES
          },
          width: 80
        },
        {
          type: 'string',
          name: 'entryView',
          header: {
            renderer: i18next.t('field.entry-view'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            editor: function (value, column, record, rowIndex, field) {
              var type = record.entryType !== 'board' ? 'string' : 'board'
              return getEditor(type)(value, column, record, rowIndex, field)
            },
            renderer: function (value, column, record, rowIndex, field) {
              var type = record.entryType !== 'board' ? 'string' : 'board'
              return getRenderer(type)(value, column, record, rowIndex, field)
            }
          },
          width: 140
        },
        {
          type: 'select',
          name: 'monitorType',
          label: true,
          header: {
            renderer: i18next.t('field.monitor-type'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            options: MONITOR_TYPES
          },
          width: 80
        },
        {
          type: 'string',
          name: 'monitorView',
          header: {
            renderer: i18next.t('field.monitor-view'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            editor: function (value, column, record, rowIndex, field) {
              var type = record.monitorType !== 'board' ? 'string' : 'board'
              return getEditor(type)(value, column, record, rowIndex, field)
            },
            renderer: function (value, column, record, rowIndex, field) {
              var type = record.monitorType !== 'board' ? 'string' : 'board'
              return getRenderer(type)(value, column, record, rowIndex, field)
            }
          },
          width: 140
        },
        {
          type: 'select',
          name: 'reportType',
          label: true,
          header: {
            renderer: i18next.t('field.report-type'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            options: REPORT_TYPES
          },
          width: 80
        },
        {
          type: 'string',
          name: 'reportView',
          header: {
            renderer: i18next.t('field.report-view'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true,
            editor: function (value, column, record, rowIndex, field) {
              var type = record.reportType !== 'custom' ? 'string' : 'script'
              return getEditor(type)(value, column, record, rowIndex, field)
            },
            renderer: function (value, column, record, rowIndex, field) {
              var type = record.reportType !== 'custom' ? 'string' : 'string'
              return getRenderer(type)(value, column, record, rowIndex, field)
            }
          },
          width: 140
        },
        {
          type: 'file',
          name: 'reportTemplate',
          header: {
            renderer: i18next.t('field.report-template'),
            group: i18next.t('field.view')
          },
          record: {
            editable: true
          },
          width: 80
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          record: {
            editable: false
          },
          width: 120
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          record: {
            editable: false
          },
          sortable: true,
          width: 180
        }
      ],
      rows: {
        selectable: {
          multiple: true
        }
      },
      sorters: [
        {
          name: 'name'
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataSets(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              tag
              partitionKeys
              active
              type
              useCase
              schedule
              timezone
              scheduleId
              summaryPeriod
              summaryScheduleId
              timeLimit
              dataKeySet {
                id
                name
              }
              entryRole {
                id
                name
              }
              supervisoryRole {
                id
                name
              }
              resolverRole {
                id
                name
              }
              normalScenario {
                id
                name
              }
              outlierScenario {
                id
                name
              }
              reviewApprovalLine {
                type
                value
                approver {
                  id
                  name
                  description
                  controlNo
                }
              }
              outlierApprovalLine {
                type
                value
                approver {
                  id
                  name
                  description
                  controlNo
                }
              }
              requiresReview
              entryType
              entryView
              monitorType
              monitorView
              reportType
              reportView
              reportTemplate
              updater {
                id
                name
              }
              updatedAt
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                subgroup
                type
                unit
                options
                quota
                spec
                stat
                agg
              }
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  async _deleteDataSet() {
    if (confirm(i18next.t('text.sure_to_x', { x: i18next.t('text.delete') }))) {
      const ids = this.grist.selected.map(record => record.id)
      if (ids && ids.length > 0) {
        const response = await client.mutate({
          mutation: gql`
            mutation ($ids: [String!]!) {
              deleteDataSets(ids: $ids)
            }
          `,
          variables: {
            ids
          }
        })

        if (!response.errors) {
          this.grist.fetch()
          notify({
            message: i18next.t('text.info_x_successfully', { x: i18next.t('text.delete') })
          })
        }
      }
    }
  }

  async _copyDataSet() {
    var selected = this.grist.selected
    if (selected.length == 0) return

    if (!confirm(i18next.t('text.sure_to_x', { x: i18next.t('text.copy') }))) return
    var response = await client.mutate({
      mutation: gql`
        mutation ($ids: [String!]!) {
          copyDataSets(ids: $ids) {
            id
          }
        }
      `,
      variables: {
        ids: selected.map(r => r.id)
      }
    })

    if (!response.errors) {
      this.grist.fetch()
    }
  }

  async _updateDataSet() {
    let patches = this.grist.dirtyRecords
    if (patches && patches.length) {
      patches = patches.map(patch => {
        let patchField: any = patch.id ? { id: patch.id } : {}
        const dirtyFields = patch.__dirtyfields__
        for (let key in dirtyFields) {
          patchField[key] = dirtyFields[key].after
        }
        if (patchField['reportTemplate'] instanceof FileList) {
          patchField['reportTemplate'] = patchField['reportTemplate'][0]
        }
        patchField.cuFlag = patch.__dirty__

        return patchField
      })

      const response = await client.mutate({
        mutation: gql`
          mutation ($patches: [DataSetPatch!]!) {
            updateMultipleDataSet(patches: $patches) {
              name
            }
          }
        `,
        variables: {
          patches
        },
        context: {
          hasUpload: true
        }
      })

      if (!response.errors) {
        this.grist.fetch()
      }
    }
  }

  async startDataCollectionSchedule(record) {
    var response = await client.mutate({
      mutation: gql`
        mutation ($dataSetId: String!) {
          startDataCollectionSchedule(dataSetId: $dataSetId) {
            scheduleId
          }
        }
      `,
      variables: {
        dataSetId: record.id
      }
    })

    const scheduleId = response.data.startDataCollectionSchedule.scheduleId
    record.scheduleId = scheduleId

    notify({
      level: 'info',
      message: `${record.scheduleId ? 'success' : 'fail'} to start data collection schedule : ${record.name}`
    })

    this.grist.fetch()
  }

  async stopDataCollectionSchedule(record) {
    var response = await client.mutate({
      mutation: gql`
        mutation ($dataSetId: String!) {
          stopDataCollectionSchedule(dataSetId: $dataSetId) {
            scheduleId
          }
        }
      `,
      variables: {
        dataSetId: record.id
      }
    })

    if (!response.errors) {
      notify({
        level: 'info',
        message: `success to stop data collection schedule : ${record.name}`
      })
    } else {
      notify({
        level: 'error',
        message: `${response.errors.map(error => error.message).join('\n')}`
      })
    }

    this.grist.fetch()
  }

  async exportHandler() {
    var headerSetting = this.grist._config.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex
      })

    let records = this.grist.data.records

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.grist._config.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            var imexKey = (column.imex as ImexConfig)!.key
            record[imexKey] = imexKey
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })
    return { header: headerSetting, data: data }
  }

  async importHandler(records) {
    openPopup(
      html`
        <data-set-importer
          .dataSets=${records}
          @imported=${() => {
            history.back()
            this.grist.fetch()
          }}
        ></data-set-importer>
      `,
      {
        backdrop: true,
        size: 'large',
        title: i18next.t('title.import data-set')
      }
    )
  }

  async *summaryScheduleTimer(exp) {
    while (exp && this.active) {
      var secs = Math.round((Number(exp) - Date.now()) / 1000)
      var positive = secs >= 0

      secs = Math.abs(secs)
      const days = Math.floor(secs / DAY)
      secs -= days * DAY
      const hours = Math.floor(secs / HOUR)
      secs -= hours * HOUR
      const minutes = Math.floor(secs / MIN)
      const seconds = secs - minutes * MIN

      yield `${positive ? '' : '- '}${days ? `${days}${i18next.t('label.days')} ` : ''}${hours ? `${hours}${i18next.t('label.hours')} ` : ''}${
        minutes ? `${minutes}${i18next.t('label.minutes')} ` : ''
      }`

      await sleep(60 * 1000)
    }
  }
}
